import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Image from "@tiptap/extension-image"
import { TableKit } from "@tiptap/extension-table"

// The text of a Textposition (position_grid/editors/_text_content.html.erb), edited with Tiptap.
//
// What the editor can produce is exactly what the server keeps (TextPositionHtml): paragraphs,
// two heading levels, bold/italic/underline/strike, lists, links, a rule, small tables and
// images. Anything else a paste brings is dropped by the editor's schema already, and whatever is
// left over is sanitized on save.
//
// The HTML goes into the hidden `input` on every change, so the drawer's save
// (position_panel_controller.js#saveLongText) posts the form as it does for a Langtext.
//
// Images are uploaded to the row first (`uploadUrl`, Billing::LineItems::ContentImagesController)
// and inserted with the token the server answers. The token is what the text keeps; `src` is only
// for showing the picture here and is stripped on save. An image without a token — pasted from a
// web page or Word — is never taken in: the save would drop it, so the editor must not show it
// (see #notePastedImages).
const ContentImage = Image.extend({
  parseHTML() {
    return [{ tag: "img[data-content-image]" }]
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      contentImage: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-content-image"),
        renderHTML: (attributes) =>
          attributes.contentImage ? { "data-content-image": attributes.contentImage } : {},
      },
    }
  },
})

export default class extends Controller {
  static targets = ["input", "mount", "button", "file", "linkBar", "linkField", "notice"]
  static values = { uploadUrl: String, messages: Object }

  connect() {
    this.editor = new Editor({
      element: this.mountTarget,
      content: this.inputTarget.value,
      extensions: [
        StarterKit.configure({
          heading: { levels: [3, 4] },
          code: false,
          codeBlock: false,
          link: { openOnClick: false, autolink: true, defaultProtocol: "https" },
        }),
        ContentImage.configure({ allowBase64: false }),
        TableKit.configure({ table: { resizable: false } }),
      ],
      editorProps: {
        attributes: {
          class: "rich-text-content min-h-48 px-3 py-2 focus:outline-none",
          "aria-label": this.messagesValue.label ?? "",
        },
        transformPastedHTML: (html) => this.notePastedImages(html),
      },
      onUpdate: () => this.sync(),
      onSelectionUpdate: () => this.refreshButtons(),
      onTransaction: () => this.refreshButtons(),
    })
    this.refreshButtons()
    // The drawer opens with the editor; the caret goes to the end of the text.
    requestAnimationFrame(() => this.editor?.commands.focus("end"))
  }

  disconnect() {
    this.editor?.destroy()
    this.editor = null
  }

  // Not `editor.isEmpty`: Tiptap counts a text that is only a table or only an image as empty, and
  // the save would then refuse it. A cleared editor is the one empty paragraph it leaves behind.
  sync() {
    const html = this.editor.getHTML()
    this.inputTarget.value = html === "<p></p>" ? "" : html
  }

  // -- toolbar ------------------------------------------------------------------

  // One action for every button: `data-rich-text-editor-command-param` names what it does.
  run(event) {
    event.preventDefault()
    const command = event.params.command
    const chain = this.editor.chain().focus()
    const commands = {
      bold: () => chain.toggleBold(),
      italic: () => chain.toggleItalic(),
      underline: () => chain.toggleUnderline(),
      heading: () => chain.toggleHeading({ level: 3 }),
      subheading: () => chain.toggleHeading({ level: 4 }),
      bulletList: () => chain.toggleBulletList(),
      orderedList: () => chain.toggleOrderedList(),
      rule: () => chain.setHorizontalRule(),
      table: () => chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }),
      addRow: () => chain.addRowAfter(),
      addColumn: () => chain.addColumnAfter(),
      deleteRow: () => chain.deleteRow(),
      deleteColumn: () => chain.deleteColumn(),
      deleteTable: () => chain.deleteTable(),
      undo: () => chain.undo(),
      redo: () => chain.redo(),
    }
    commands[command]?.().run()
  }

  // A link asks for its address in a bar under the toolbar — not a browser prompt, which would
  // block the page. An empty address removes the link under the caret.
  link(event) {
    event.preventDefault()
    this.linkFieldTarget.value = this.editor.getAttributes("link").href ?? ""
    this.linkBarTarget.hidden = false
    this.linkFieldTarget.focus()
  }

  linkKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      event.stopPropagation()
      this.applyLink()
    } else if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.closeLinkBar()
    }
  }

  applyLink(event) {
    event?.preventDefault()
    const href = this.linkFieldTarget.value.trim()
    const chain = this.editor.chain().focus().extendMarkRange("link")
    if (href === "") chain.unsetLink().run()
    else chain.setLink({ href }).run()
    this.linkBarTarget.hidden = true
  }

  closeLinkBar(event) {
    event?.preventDefault()
    this.linkBarTarget.hidden = true
    this.editor.commands.focus()
  }

  // Pressed state for the marks and blocks; the table buttons only inside a table.
  refreshButtons() {
    if (!this.editor) return

    const inTable = this.editor.isActive("table")
    this.buttonTargets.forEach((button) => {
      const { active, tableOnly } = button.dataset
      if (active) button.setAttribute("aria-pressed", String(this.isActive(active)))
      if (tableOnly !== undefined) button.hidden = !inTable
    })
  }

  isActive(name) {
    if (name === "heading") return this.editor.isActive("heading", { level: 3 })
    if (name === "subheading") return this.editor.isActive("heading", { level: 4 })
    return this.editor.isActive(name)
  }

  // -- images -------------------------------------------------------------------

  pickImage(event) {
    event.preventDefault()
    this.fileTarget.click()
  }

  async upload() {
    const file = this.fileTarget.files[0]
    this.fileTarget.value = ""
    if (!file) return

    const body = new FormData()
    body.append("image", file)
    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
        },
        body,
      })
      const answer = await response.json().catch(() => ({}))
      if (!response.ok) return this.notify(answer.error ?? this.messagesValue.upload_failed)

      this.editor
        .chain()
        .focus()
        .setImage({ src: answer.url, alt: "", contentImage: answer.token })
        .run()
    } catch (error) {
      Honeybadger.notify(error, { context: { url: this.uploadUrlValue } })
      this.notify(this.messagesValue.upload_failed)
    }
  }

  // A paste's foreign images are dropped by the schema (ContentImage#parseHTML); say so, or the
  // user takes the picture for inserted.
  notePastedImages(html) {
    const pasted = new DOMParser().parseFromString(html, "text/html")
    if (pasted.querySelector("img:not([data-content-image])")) {
      this.notify(this.messagesValue.pasted_image_dropped)
    }
    return html
  }

  notify(message) {
    if (!message || !this.hasNoticeTarget) return

    clearTimeout(this.noticeTimer)
    this.noticeTarget.textContent = message
    this.noticeTarget.hidden = false
    this.noticeTimer = setTimeout(() => (this.noticeTarget.hidden = true), 5000)
  }
}
